import type { Product } from "../data";
import { SIGNUP_URL, WALKTHROUGH_URL } from "../data";
import ProductVisual from "./ProductVisual";
import { track } from "@/lib/analytics";

interface ProductBlockProps {
  product: Product;
  index: number;
}

export default function ProductBlock({ product, index }: ProductBlockProps) {
  const reversed = index % 2 === 1;

  return (
    <section
      id={product.id}
      className={`relative py-16 md:py-20 px-4 md:px-8 border-t border-background-300/20 scroll-mt-20 md:scroll-mt-24 ${
        index % 2 === 1 ? "bg-background-100" : "bg-background-50"
      }`}
    >
      <div className="max-w-6xl mx-auto">
        <div
          className={`reveal flex flex-col lg:flex-row items-center gap-10 lg:gap-16 ${
            reversed ? "lg:flex-row-reverse" : ""
          }`}
        >
          <div className="flex-1 w-full">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary-500/25 bg-primary-500/10 px-4 py-1.5 mb-5">
              <i className="ri-flashlight-fill text-xs text-primary-300" aria-hidden></i>
              <span className="text-xs font-semibold text-primary-300 tracking-wide">
                {product.eyebrow}
              </span>
            </div>

            <h2 className="font-heading text-3xl sm:text-4xl font-medium text-foreground-950 leading-tight tracking-tight">
              {product.title}
            </h2>

            <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed max-w-xl">
              {product.description}
            </p>

            <div className="mt-8 space-y-4">
              {product.features.map((feature) => (
                <div key={feature.title} className="flex items-start gap-3.5">
                  <span className="w-9 h-9 shrink-0 rounded-lg flex items-center justify-center bg-accent-500/12 border border-accent-500/25">
                    <i className={`${feature.icon} text-base text-accent-300`} aria-hidden></i>
                  </span>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-950">{feature.title}</h3>
                    <p className="text-sm text-foreground-700 leading-relaxed mt-0.5">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-9 flex flex-col sm:flex-row items-center gap-4">
              <a
                href={WALKTHROUGH_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  track("book_demo_clicked", { location: `product_${product.id}_walkthrough` })
                }
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md border border-background-300/60 text-foreground-900 text-sm font-semibold px-6 py-3 hover:bg-background-100/70 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-event-line" aria-hidden></i>
                Book a walkthrough
              </a>
              <a
                href={SIGNUP_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track("try_joble_clicked", { location: `product_${product.id}_cta` })}
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold px-6 py-3 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                Try Joble Now
                <i
                  className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"
                  aria-hidden
                ></i>
              </a>
            </div>
          </div>

          <div className="flex-1 w-full">
            <ProductVisual variant={product.visual} name={product.name} caption={product.caption} />
          </div>
        </div>
      </div>
    </section>
  );
}